import { useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import { BlockContent } from '@/components/renderers/BlockContent'
import { REVIEW_CARD_DIMENSIONS } from '@/components/renderers/ReviewCard'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

const marquee = z.object({
  _type: z.literal('marquee'),
  speed: z.number(),
  cards: z.array(
    z.object({
      _key: z.string(),
      content: z.array(z.unknown()),
    })
  ),
})

export const Marquee = (props: {
  node: PortableTextTypeComponentProps<unknown>
  sectionName: string
  contentWidth: number
}) => {
  const value = useMemo(() => marquee.parse(props.node.value), [props.node.value])
  const [windowWidth, setWindowWidth] = useState<number>(0)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth)
      const handleResize = () => {
        setWindowWidth(window.innerWidth)
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [])

  const { cards } = useMemo(() => {
    const noOfchildren = value.cards.length
    if (noOfchildren === 0) {
      return {
        cards: [],
      }
    }
    const repetitions = windowWidth
      ? Math.ceil(windowWidth / (noOfchildren * REVIEW_CARD_DIMENSIONS.width)) + 1
      : 0
    return {
      cards: new Array(repetitions).fill(null).flatMap(() => value.cards),
    }
  }, [windowWidth, value.cards])

  return (
    <div
      className={twMerge(
        'relative -mx-6 flex h-full w-[100vw] flex-row overflow-hidden md:-mx-12 lg:-mx-20'
      )}
    >
      <div className="flex animate-marquee flex-row gap-x-6 hover:[animation-play-state:paused]">
        {cards.map((card, index) => (
          <BlockContent
            key={`${card._key}-${index}`}
            blocks={card.content}
            sectionName={props.sectionName}
            width={REVIEW_CARD_DIMENSIONS.width}
          />
        ))}
      </div>
    </div>
  )
}
